import { formatUnits } from 'ethers'

import { FeePaymentOption } from '@ambire-common/libs/estimate/interfaces'

/**
 * The relayer reports the Gas Tank balance as one entry per (token, chain) —
 * `data.gasTank.balance[]` in `/v2/identity/:addr/portfolio-additional`, mapped
 * 1:1 into `gasTankTokens` (PortfolioController) and then into a fee option per
 * entry (EstimationController). A user who topped up USDC on two chains
 * therefore gets two "USDC · Gas Tank" rows in "Pay fee with" that look
 * identical, because the row hides the network for Gas Tank options and the
 * number it shows is the fee, not the balance.
 *
 * Users think of the tank as a single pot, so we fold the entries of one symbol
 * into a single row: the row is backed by the entry we can actually spend, and
 * displays the combined balance of the whole group.
 */
export type GasTankAggregatedBalance = {
  /** Combined spendable balance of the group, in token units. */
  amount: number
  /** Combined spendable balance in USD, or null when no entry has a price. */
  amountUsd: number | null
  /**
   * The largest single entry, in token units — the most one fee can actually
   * draw. A fee is settled against ONE (token, chain) entry; the relayer does
   * not pool them. Showing `amount` as what's available therefore over-promises
   * whenever the group is split, which reads as a contradiction: "Available:
   * $0.86" next to "Insufficient amount" for a $0.69 fee.
   */
  maxSpendable: number
  /** `maxSpendable` in USD, or null when that entry has no price. */
  maxSpendableUsd: number | null
  /** How many (token, chain) entries were folded into this row. */
  entryCount: number
}

export const getFeeOptionUsdPrice = (feeOption: FeePaymentOption): number | null =>
  feeOption.token.priceIn?.find(({ baseCurrency }) => baseCurrency === 'usd')?.price ?? null

export const getFeeOptionBalance = (feeOption: FeePaymentOption): number =>
  Number(formatUnits(feeOption.availableAmount, feeOption.token.decimals))

const getGroupKey = (feeOption: FeePaymentOption) =>
  `${feeOption.paidBy}:${feeOption.token.symbol.toLowerCase()}`

/**
 * Collapses the Gas Tank fee options of the same token symbol into one option.
 * Non-Gas-Tank options are returned untouched, in their original order.
 *
 * @param canCoverFee tells whether an option has enough balance for the fee.
 *   Passed in because the two Estimation flavours judge it differently (the
 *   common one also accounts for the transferred-token case).
 */
const collapseGasTankFeeOptions = (
  feeOptions: FeePaymentOption[],
  canCoverFee: (feeOption: FeePaymentOption) => boolean
): {
  options: FeePaymentOption[]
  /** Keyed by the representative option of each collapsed group. */
  gasTankBalances: Map<FeePaymentOption, GasTankAggregatedBalance>
} => {
  const groups = new Map<string, FeePaymentOption[]>()
  // Preserves the incoming order: a plain option, or the first-seen group key.
  const order: { option?: FeePaymentOption; groupKey?: string }[] = []

  feeOptions.forEach((feeOption) => {
    if (!feeOption.token.flags.onGasTank) {
      order.push({ option: feeOption })
      return
    }

    const groupKey = getGroupKey(feeOption)
    const group = groups.get(groupKey)

    if (group) {
      group.push(feeOption)
      return
    }

    groups.set(groupKey, [feeOption])
    order.push({ groupKey })
  })

  const gasTankBalances = new Map<FeePaymentOption, GasTankAggregatedBalance>()
  const options: FeePaymentOption[] = []

  order.forEach(({ option, groupKey }) => {
    if (option) {
      options.push(option)
      return
    }

    const group = groups.get(groupKey!)!
    // Spend the entry that can actually cover the fee; among equals (or when
    // none can cover it) take the fattest one, so the row is the closest to
    // being payable and "Insufficient amount" only shows when it truly is.
    const representative = group.reduce((best, candidate) => {
      const bestCanCover = canCoverFee(best)
      const candidateCanCover = canCoverFee(candidate)
      if (bestCanCover !== candidateCanCover) return candidateCanCover ? candidate : best

      return getFeeOptionBalance(candidate) > getFeeOptionBalance(best) ? candidate : best
    })

    let amount = 0
    let amountUsd: number | null = null
    let maxSpendable = 0
    let maxSpendableUsd: number | null = null

    group.forEach((entry) => {
      const balance = getFeeOptionBalance(entry)
      const price = getFeeOptionUsdPrice(entry)

      amount += balance
      if (price !== null) amountUsd = (amountUsd ?? 0) + balance * price

      if (balance > maxSpendable) {
        maxSpendable = balance
        maxSpendableUsd = price !== null ? balance * price : null
      }
    })

    options.push(representative)
    gasTankBalances.set(representative, {
      amount,
      amountUsd,
      maxSpendable,
      maxSpendableUsd,
      entryCount: group.length
    })
  })

  return { options, gasTankBalances }
}

export default collapseGasTankFeeOptions
